// src/hooks/useLlmResponseFeedback.ts
import { useState } from "react";
import { fetchWithAuth } from "../../../../api-client/api_client";
import { APIEndpointMethod } from "../../../../models/Entities";
import { showErrorToast, showSuccessToast } from "../../../shared/Toasts";

export enum LLMResponseFeedbackChoices {
  HELPFUL = "HELPFUL",
  NOT_HELPFUL = "NOT_HELPFUL",
}

interface FeedbackPayload {
  feedback: LLMResponseFeedbackChoices;
  response_id: string;
}

export function useLlmResponseFeedback() {
  const [feedbackState, setFeedbackState] = useState<LLMResponseFeedbackChoices | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitFeedback = ({ feedback, response_id }: FeedbackPayload) => {
    // Prevent multiple submissions or changing feedback
    if (feedbackState !== null || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    fetchWithAuth({
      path: "/ai/blueprint-editor/feedback/",
      method: APIEndpointMethod.POST,
      headers: { "Content-Type": "application/json" },
      body: {
        feedback,
        response_id,
      },
      onResponse: () => {
        setIsSubmitting(false);
        setFeedbackState(feedback);
        showSuccessToast("Thank you for your feedback!");
      },
      onError: (error) => {
        setIsSubmitting(false);
        error
          ?.json()
          .then((value: string | Record<string, any>) => {
            let errorMessage = "";
            if (typeof value == "string") {
              errorMessage = value;
            } else {
              errorMessage = value?.non_field_errors ?? JSON.stringify(value);
            }
            showErrorToast(`Failed to submit feedback. Error: ${errorMessage}`);
          })
          .catch(() => {
            showErrorToast("Failed to submit feedback");
          });
      },
    });
  };

  const handleHelpfulFeedback = (responseId: string) => {
    submitFeedback({
      feedback: LLMResponseFeedbackChoices.HELPFUL,
      response_id: responseId,
    });
  };

  const handleNotHelpfulFeedback = (responseId: string) => {
    submitFeedback({
      feedback: LLMResponseFeedbackChoices.NOT_HELPFUL,
      response_id: responseId,
    });
  };

  return {
    feedbackState,
    isSubmitting,
    handleHelpfulFeedback,
    handleNotHelpfulFeedback,
  };
}
